import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../ManagedServices/ManagedCDN.css';

const CentralizedMonitoring = () => {
    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>Managed Centralized Logging & Monitoring - 24/7 NOC Services | Epicfinal</title>
                <meta name="title" content="Managed Centralized Logging & Monitoring - 24/7 NOC Services | Epicfinal" />
                <meta name="description" content="Managed monitoring service at $490/mo. 24/7 server & service monitoring, smart alerting, first-line response, cloud dashboard & monthly reports. Up to 8 hours work/month included." />
                <meta name="keywords" content="centralized monitoring, managed monitoring, server monitoring, NOC services, infrastructure monitoring, alert management, service monitoring, cloud monitoring, 24/7 monitoring, performance monitoring, uptime monitoring, resource monitoring, log management" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="Epicfinal" />
                <link rel="canonical" href="https://epicfinal.com/centralized-monitoring" />

                {/* Language & Locale */}
                <html lang="en" />
                <meta property="og:locale" content="en_US" />
                <link rel="alternate" href="https://epicfinal.com/centralized-monitoring" hrefLang="en" />
                <link rel="alternate" href="https://epicfinal.com/centralized-monitoring" hrefLang="x-default" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://epicfinal.com/centralized-monitoring" />
                <meta property="og:title" content="Managed Centralized Logging & Monitoring | Epicfinal" />
                <meta property="og:description" content="24/7 managed monitoring at $490/mo. Smart alerting, first-line response, cloud dashboard & monthly reports." />
                <meta property="og:image" content="https://epicfinal.com/android-chrome-512x512.png" />
                <meta property="og:image:alt" content="Epicfinal Centralized Monitoring - 24/7 NOC Services" />
                <meta property="og:site_name" content="Epicfinal" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://epicfinal.com/centralized-monitoring" />
                <meta name="twitter:title" content="Managed Centralized Logging & Monitoring | Epicfinal" />
                <meta name="twitter:description" content="24/7 managed monitoring at $490/mo. Smart alerting & first-line response." />
                <meta name="twitter:image" content="https://epicfinal.com/android-chrome-512x512.png" />

                {/* Schema.org Structured Data */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "name": "Managed Centralized Logging & Monitoring",
                        "description": "Fully managed monitoring service with smart agent deployment, 24/7 alert monitoring, first-line response, cloud dashboard, and monthly status reports",
                        "provider": {"@type": "Organization", "name": "Epicfinal", "url": "https://epicfinal.com"},
                        "serviceType": "Infrastructure Monitoring",
                        "areaServed": "Worldwide",
                        "offers": {"@type": "Offer", "price": "490.00", "priceCurrency": "USD", "priceValidUntil": "2025-12-31", "availability": "https://schema.org/InStock"},
                        "url": "https://epicfinal.com/centralized-monitoring"
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Managed Centralized Logging & Monitoring",
                        "url": "https://epicfinal.com/centralized-monitoring",
                        "breadcrumb": {
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://epicfinal.com"},
                                {"@type": "ListItem", "position": 2, "name": "Managed Services", "item": "https://epicfinal.com/managed-services"},
                                {"@type": "ListItem", "position": 3, "name": "Centralized Monitoring", "item": "https://epicfinal.com/centralized-monitoring"}
                            ]
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {"@type": "Question", "name": "What does centralized monitoring include?", "acceptedAnswer": {"@type": "Answer", "text": "The service includes smart monitoring agent deployment, continuous resource monitoring (CPU, RAM, disk, network), service health checks, 24/7 alert monitoring, first-line response, cloud-based dashboard, and monthly status reports."}},
                            {"@type": "Question", "name": "How much work is included per month?", "acceptedAnswer": {"@type": "Answer", "text": "The $490/month plan includes up to 8 hours of hands-on work per month for alert response, troubleshooting, and maintenance tasks."}},
                            {"@type": "Question", "name": "Is monitoring platform licensing included?", "acceptedAnswer": {"@type": "Answer", "text": "No, the service covers management only. Monitoring platform licenses and any cloud provider costs are not included and billed separately."}}
                        ]
                    }
                `}</script>
            </Helmet>

            <div className="managed-cdn-page">
                <section className="cdn-hero">
                    <div className="container">
                        <div className="cdn-hero-content">
                            <div className="cdn-icon-badge">
                                <i className="las la-chart-line"></i>
                            </div>
                            <h1 className="cdn-hero-title">
                                Managed Centralized Logging & Monitoring
                            </h1>
                            <p className="cdn-hero-subtitle">
                                Managed Server & Service Monitoring
                            </p>
                            <p className="cdn-hero-description">
                                We provide fully managed monitoring for your servers and critical services, using a smart monitoring agent and a cloud-based dashboard. Our team configures thresholds, watches alerts and handles first-line response so issues are detected early and acted on quickly.
                            </p>
                            <div className="cdn-hero-buttons">
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-centralized-logging-and-monitoring" 
                                   className="btn-cdn btn-primary" target="_blank" rel="noopener noreferrer">
                                    Order Now - $490.00/mo
                                </a>
                                <Link to="/contact1" className="btn-cdn btn-secondary">
                                    Talk to Expert
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-features">
                    <div className="container">
                        <div className="cdn-section-header">
                            <h2>Comprehensive Monitoring Solution</h2>
                            <p>Proactive monitoring with expert response</p>
                        </div>
                        <div className="cdn-features-grid">
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-server"></i>
                                </div>
                                <h3>Resource Monitoring</h3>
                                <p>Continuous tracking of CPU, RAM, disk, and network usage across your infrastructure.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-heartbeat"></i>
                                </div>
                                <h3>Service Health Checks</h3>
                                <p>Monitor web servers, databases, applications, and critical services for uptime.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-bell"></i>
                                </div>
                                <h3>Alert Management</h3>
                                <p>Smart alerting with threshold configuration and escalation procedures.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-user-shield"></i>
                                </div>
                                <h3>First-Line Response</h3>
                                <p>Our team handles initial triage and response to critical alerts.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-chart-area"></i>
                                </div>
                                <h3>Cloud Dashboard</h3>
                                <p>Real-time visibility into your infrastructure with detailed metrics and graphs.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-file-alt"></i>
                                </div>
                                <h3>Monthly Reports</h3>
                                <p>Comprehensive status summaries with performance insights and recommendations.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-included">
                    <div className="container">
                        <div className="cdn-section-header">
                            <h2>What's Included</h2>
                            <p>Full monitoring and alert management</p>
                        </div>
                        <div className="cdn-included-grid">
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> Monitoring Setup</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> Smart monitoring agent deployment</li>
                                    <li><i className="las la-angle-right"></i> Resource monitoring configuration</li>
                                    <li><i className="las la-angle-right"></i> Service health checks</li>
                                    <li><i className="las la-angle-right"></i> Custom threshold settings</li>
                                </ul>
                            </div>
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> Alert Handling</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> alert monitoring</li>
                                    <li><i className="las la-angle-right"></i> Alert triage & classification</li>
                                    <li><i className="las la-angle-right"></i> First-line response</li>
                                    <li><i className="las la-angle-right"></i> Escalation procedures</li>
                                </ul>
                            </div>
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> Reports & Support</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> Monthly status summary</li>
                                    <li><i className="las la-angle-right"></i> Performance analytics</li>
                                    <li><i className="las la-angle-right"></i> Up to 8 hours work/month</li>
                                    <li><i className="las la-angle-right"></i> Expert technical support</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-pricing">
                    <div className="container">
                        <div className="cdn-pricing-card">
                            <div className="cdn-pricing-header">
                                <h2>Centralized Monitoring Service</h2>
                                <p>Complete monitoring and alert management</p>
                            </div>
                            <div className="cdn-pricing-body">
                                <div className="cdn-pricing-amount">
                                    <span className="cdn-price">$490.00</span>
                                    <span className="cdn-period">USD / Monthly</span>
                                </div>
                                <ul className="cdn-pricing-features">
                                    <li><i className="las la-check"></i> Smart monitoring agent deployment</li>
                                    <li><i className="las la-check"></i> Continuous resource monitoring</li>
                                    <li><i className="las la-check"></i> Service & uptime checks</li>
                                    <li><i className="las la-check"></i> 24/7 alert monitoring</li>
                                    <li><i className="las la-check"></i> Alert triage & response</li>
                                    <li><i className="las la-check"></i> Cloud-based dashboard</li>
                                    <li><i className="las la-check"></i> Monthly status reports</li>
                                    <li><i className="las la-check"></i> Up to 8 hours work/month</li>
                                </ul>
                                <div className="cdn-pricing-note">
                                    <i className="las la-info-circle"></i>
                                    <p><strong>Note:</strong> Does not include monitoring platform licenses or cloud provider costs.</p>
                                </div>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-centralized-logging-and-monitoring" 
                                   className="cdn-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started Now
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-cta">
                    <div className="container">
                        <div className="cdn-cta-content">
                            <h2>Ready for Proactive Monitoring?</h2>
                            <p>Proactive watch for your infrastructure and respond to issues quickly</p>
                            <div className="cdn-cta-buttons">
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-centralized-logging-and-monitoring" 
                                   className="btn-cdn btn-light" target="_blank" rel="noopener noreferrer">
                                    Order Service
                                </a>
                                <a href="https://meetings.hubspot.com/viacheslav-seifer" 
                                   className="btn-cdn btn-outline" target="_blank" rel="noopener noreferrer">
                                    Schedule Consultation
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default CentralizedMonitoring;

